import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useMemo, useEffect } from "react";
import { StoreState } from "../models/states/StoreState";
import { RecallType } from "../piping/RecallType";
import { getRecall } from "../redux/reducers/recallReducer";
import { evaluateRecalls } from "../redux/thunks/recall";
import { Piping } from "../piping";

export default function useRecall<T extends string | ReadonlyArray<string>>(
    text: T,
    type: RecallType = RecallType.Text,
): T {
    const dispatch = useDispatch();
    const interview = useSelector((state: StoreState) => state.interview, shallowEqual);
    const recalled = useSelector((state: StoreState) => {
        if (text == null) return text;
        if (typeof text === "string") return getRecall(state.recall, text, type);
        return (text as ReadonlyArray<string>).map((t) => getRecall(state.recall, t, type));
    }, shallowEqual);

    // Evaluate recalls synchronously during render using useMemo
    // This ensures recalls are evaluated before pageReady is triggered
    const evaluatedValue = useMemo(() => {
        if (text == null) return text;
        if (typeof text === "string") {
            // If cached, return cached value
            if (recalled !== undefined) return recalled;
            // Otherwise evaluate synchronously
            return Piping.evaluateRecalls(interview, text, type);
        } else {
            // If cached, return cached value
            if (recalled !== undefined) return recalled;
            // Otherwise evaluate synchronously
            return (text as ReadonlyArray<string>).map((t) =>
                Piping.evaluateRecalls(interview, t, type),
            ) as unknown as T;
        }
    }, [text, type, interview, recalled]);

    // Update cache/state asynchronously in useEffect to avoid React warnings
    useEffect(() => {
        if (typeof text === "string" && recalled === undefined && text && type) {
            dispatch(evaluateRecalls(text, type));
        } else if (typeof text === "object" && recalled === undefined && text && type) {
            (text as ReadonlyArray<string>).forEach((t) => {
                dispatch(evaluateRecalls(t, type));
            });
        }
    }, [text, type, recalled, dispatch]);

    // Return the evaluated value (from cache or computed synchronously)
    return evaluatedValue;
}
